import { Box, Typography, Grid, styled } from '@mui/material';
import { ComponentProps } from 'react';

import { Attribute, Product } from '@app/types/catalog';
import {
  formatAnnualSalary,
  getAnnualSalary,
  isAgencyProduct,
} from '@app/utils/catalog';
import { convertNewLineToBr } from '@app/utils/components';
import { formatDate } from '@app/utils/format';

const ColBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const RowBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
});

function DetailGridItem({
  label,
  children,
  ...props
}: ComponentProps<typeof Grid> & { label: string }) {
  return (
    <Grid
      item
      xs={6}
      display="flex"
      flexDirection="column"
      gap={1}
      {...props}
      pr={24}
    >
      <Typography fontWeight={700}>{label}</Typography>
      <ColBox>{children}</ColBox>
    </Grid>
  );
}

export interface FullTimeProductDetailComponentProps {
  attributeClinicalDepartmentList?: Attribute[];
  attributeJobTypeList?: Attribute[];
  product: Product;
}

export function FullTimeProductDetailComponent({
  product,
  attributeClinicalDepartmentList,
  attributeJobTypeList,
}: FullTimeProductDetailComponentProps) {
  const clinicalDepartmentList =
    attributeClinicalDepartmentList
      ?.filter((c) => product?.attributes.some((a) => a.attributeId === c.id))
      .map((c) => c.items?.[0].value ?? '') ?? [];
  const jobTypeList =
    attributeJobTypeList
      ?.filter((c) => product?.attributes.some((a) => a.attributeId === c.id))
      .map((c) => c.name) ?? [];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container columnSpacing={2} rowSpacing={4}>
        <DetailGridItem label="案件区分">
          <Typography>{product.category.name}求人</Typography>
        </DetailGridItem>
        <DetailGridItem label="掲載期間">
          <Typography>
            {formatDate(product.publication?.since)}～
            {formatDate(product.publication?.until)}
          </Typography>
        </DetailGridItem>
        <DetailGridItem label="キャッチコピー">
          <Typography>
            {convertNewLineToBr(product.customFields?.catchphrase ?? '')}
          </Typography>
        </DetailGridItem>
        <DetailGridItem label="募集科目">
          <Typography>{clinicalDepartmentList.join('、')}</Typography>
        </DetailGridItem>
        <DetailGridItem label="勤務先">
          {isAgencyProduct(product) && (
            <Typography>{product.customFields?.organizationName}</Typography>
          )}
          <Typography>〒{product.customFields?.workPostalCode}</Typography>
          <RowBox gap={1}>
            <Typography>
              {product.locations.find((l) => l.type === 'prefecture')?.name}
            </Typography>
            <Typography>
              {product.locations.find((l) => l.type === 'city')?.name}
            </Typography>
          </RowBox>
          <Typography>{product.customFields?.workAddress1}</Typography>
          <Typography>{product.customFields?.workAddress2}</Typography>
          <Typography>
            {convertNewLineToBr(product.customFields?.access)}
          </Typography>
        </DetailGridItem>
        <DetailGridItem label="仕事種別">
          <Typography>{jobTypeList.join('、')}</Typography>
        </DetailGridItem>
        <DetailGridItem label="管理医師案件">
          <Typography>
            {product.customFields?.isManagementPosition ? 'あり' : 'なし'}
          </Typography>
        </DetailGridItem>
        <DetailGridItem label="年収">
          <Typography>
            {formatAnnualSalary(getAnnualSalary(product))}
          </Typography>
        </DetailGridItem>
        <DetailGridItem label="詳細情報">
          <Typography>{convertNewLineToBr(product.description)}</Typography>
        </DetailGridItem>
      </Grid>
    </Box>
  );
}
